import React, { useMemo } from 'react'
import log from 'loglevel'
import { RiskFilter } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Grid } from 'src/components/Reusable/Grid'
import { TabType, Tabs } from 'src/components/Reusable/Tabs'
import {
  TabPanelButtonGroup,
  TabPanelNoteHeader,
  TabPanelNoteItem,
} from 'src/components/Reusable/Tabs/TabNotes/styles'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useVendors, useVendorsRequest } from 'src/pages/Vendors/vendors.hooks'
import { ModalFormId } from 'src/components/ModalForms'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import {
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons/StandardButton'
import { E2E_IDS } from 'src/Utils/testIds'
import { VendorsGrid } from 'src/pages/Vendors/grid/VendorsGrid'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { useControlsbyIds } from 'src/components/async/model/control/useControls'
import { useRisksContext } from 'src/pages/Risks/context/RisksContext'
import { RisksControlsGridRow } from '../grid/RisksControlsGridRow'
import { NoItemsLinked } from '../risks.styles'
import { AddVendorsToRiskModal } from '../modals/AddVendorsToRiskModal'
import { RisksControlsGridHeader } from '../grid/RisksControlsGridHeader'
import { toRiskControl } from '../risks.helpers'
import { RiskControl } from '../risks.constants'

export const RisksTabs = ({ risk }: { risk: Risk }): JSX.Element => {
  const riskId = risk.getId()
  const ids = risk.getControlIds()
  const controlIds = ids.length > 0 ? ids.split(',') : []
  const { controlSortColumn, controlSortType } = useRisksContext()
  const riskObj = risk instanceof Risk ? risk.toObject() : undefined
  const canLinkControls = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_LINK_RISK_CONTROLS,
    riskObj,
  )
  const canLinkVendors = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_LINK_RISK_VENDORS,
    riskObj,
  )
  const { data: controls } = useControlsbyIds(controlIds, true)
  const controlsSorted = useMemo(() => {
    const riskControlObj = controls?.getItemsList().map((control) => {
      return toRiskControl(risk, control)
    })
    if (!controls || !controlSortColumn) return riskControlObj || []

    return riskControlObj?.sort((a, b) => {
      const aValue =
        a[
          controlSortColumn.toLowerCase().replaceAll(' ', '_') as keyof typeof a
        ] ?? ''
      const bValue =
        b[
          controlSortColumn.toLowerCase().replaceAll(' ', '_') as keyof typeof b
        ] ?? ''

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue)
        return controlSortType === 'asc' ? comparison : -comparison
      }

      if (aValue < bValue) return controlSortType === 'asc' ? -1 : 1
      if (aValue > bValue) return controlSortType === 'asc' ? 1 : -1

      return 0
    })
  }, [controls, controlSortColumn, controlSortType, risk])

  const vendorsReq = useVendorsRequest()
  vendorsReq.setRiskFilter(new RiskFilter().setRiskId(riskId))
  const { data, isLoading, error } = useVendors(vendorsReq)
  const vendors = data?.getVendorsList() || []

  let vendorsBody
  if (isLoading) {
    vendorsBody = <TableListLoader />
  } else if (error) {
    log.error(`Error loading vendors for risk id ${riskId}:`, error)
    vendorsBody = (
      <NoItemsLinked>
        Sorry, there was an issue loading the related vendors. Please try again.
      </NoItemsLinked>
    )
  } else if (!data || vendors.length === 0) {
    vendorsBody = <NoItemsLinked>No vendors linked to this risk.</NoItemsLinked>
  } else {
    vendorsBody = <VendorsGrid vendors={vendors} />
  }

  const tabs: TabType[] = [
    {
      title: 'Mitigating (compensating) controls',
      count: (controlsSorted ?? []).length,
      body: (
        <>
          {(controlsSorted ?? []).length > 0 ? (
            <Grid
              pb="s"
              gridTemplateColumns="min-content min-content minmax(min-content, 1fr) minmax(min-content, 1fr) repeat(1, auto)"
            >
              <RisksControlsGridHeader />
              {(controlsSorted ?? []).map((riskcontrol: RiskControl) => (
                <RisksControlsGridRow
                  key={riskcontrol.id}
                  riskcontrol={riskcontrol}
                  canLinkControls={canLinkControls}
                />
              ))}
            </Grid>
          ) : (
            <NoItemsLinked>No controls linked to this risk.</NoItemsLinked>
          )}
        </>
      ),
      toolTipBody: 'Controls relevant to managing this risk.',
      note: (
        <TabPanelNoteItem>
          <TabPanelNoteHeader>
            <div></div>
            <TabPanelButtonGroup>
              <StandardOpenModalButton
                modalId={ModalFormId.LINK_CONTROL_TO_RISK}
                variant={StandardButtonVariant.PRIMARY}
                size={StandardButtonSize.MEDIUM}
                text="Link Controls"
                testId={E2E_IDS.RISK_LINK_ADD_CONTROL}
                isDisabled={!canLinkControls}
                disabledReason="You do not have permission to link controls to this risk."
              />
            </TabPanelButtonGroup>
          </TabPanelNoteHeader>
        </TabPanelNoteItem>
      ),
    },
  ]
  const vendorsTab = {
    title: 'Related vendors',
    count: vendors.length,
    body: vendorsBody,
    note: (
      <TabPanelNoteItem>
        <TabPanelNoteHeader>
          <div></div>
          <TabPanelButtonGroup>
            <StandardOpenModalButton
              modalId={ModalFormId.LINK_VENDOR_TO_RISK}
              variant={StandardButtonVariant.PRIMARY}
              size={StandardButtonSize.MEDIUM}
              text="Link Vendors"
              isDisabled={!canLinkVendors}
              disabledReason="You do not have permission to link vendors to this risk."
            />
          </TabPanelButtonGroup>
        </TabPanelNoteHeader>
      </TabPanelNoteItem>
    ),
  }
  if (IsGatekeeperEnabled(GatekeeperFlag.VENDOR_MANAGEMENT)) {
    tabs.push(vendorsTab)
  }

  const vendorIds = vendors.map((vendor) => vendor.getId())

  return (
    <>
      <Tabs tabs={tabs} />
      {/* modals */}
      <AddVendorsToRiskModal riskId={riskId} existingVendorIds={vendorIds} />
    </>
  )
}
